import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Button,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import button from "assets/theme/components/button";
import { color } from "three/src/nodes/tsl/TSLCore.js";

const ICON_URLS = {
  teal: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG",
  gray: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Gray.PNG",
  white:
    "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-White.PNG",
};

const alpha = (hex = "#000000", a) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};


function useContainerWidth(ref) {
  const [width, setWidth] = useState(800);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

const SampleQuestion = ({ text, color }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 6,
      marginBottom: 6,
    }}
  >
    <span style={{ color, marginTop: 2, fontSize: 12 }}>→</span>
    <span style={{ fontSize: 12.5, fontWeight: 600, color }}>{text}</span>
  </div>
);

const AnswerBubble = ({ bg, textColor, borderColor, brand, inputBg,buttonBg,secTextColor }) => (
  <div
    style={{
      background: bg,
      border: `1px solid ${borderColor}`,
      borderRadius: 10,
      padding: "10px 12px",
      fontSize: 12,
      color: textColor,
      lineHeight: 1.55,
    }}
  >
    We offer a range of AI-powered solutions including{" "}
    <strong>smart search</strong>, <strong>chatbot integration</strong>, and{" "}
    <strong>analytics</strong> to help your business grow.
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: 8,
        flexWrap: "wrap",
        gap: 4,
      }}
    >
      {[["↺ Clear", "📋 Copy"], ["👍 Good Answer", "👎 Bad Answer"]].map((grp, gi) => (
        <div key={gi} style={{ display: "flex", gap: 4 }}>
          {grp.map((label) => (
            <span
              key={label}
              style={{
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 5,
                border: `1px solid ${borderColor}`,
                background: buttonBg,
                color: "#ffffff",
                cursor: "default",
              }}
            >
              {label}
            </span>
          ))}
        </div>
      ))}
    </div>

    <div
      style={{
        display: "flex",
        gap: 6,
        marginTop: 8,
        overflow: "hidden",
        flexWrap: "wrap",
      }}
    >
        <div style={{width:"100%"}}>
                    <p style={{color:"#c0c0c0"}}>Answer based on the following sources:</p>
        </div>

      {["Services page", "About us"].map((t) => (
        <div
          key={t}
          style={{
            flex: "0 0 110px",
            background: inputBg,
            border: `1px solid ${borderColor}`,
            borderRadius: 7,
            padding: "6px 8px",
          }}
        >
          <div
            style={{ fontSize: 11, fontWeight: 600, color: textColor, marginBottom: 2 }}
          >
            {t}
          </div>
          <div style={{ fontSize: 10, color: textColor, opacity: 0.5 }}>
            yoursite.com
          </div>
        </div>
      ))}
    </div>
  </div>
);

const InputArea = ({ bg, inputBg, borderColor, textColor, brand, suggestions }) => (
  <div
    style={{
      padding: "8px 10px",
      borderTop: `1px solid ${borderColor}`,
      background: bg,
      flexShrink: 0,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: inputBg,
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        padding: "5px 8px",
      }}
    >
      <div style={{ flex: 1, fontSize: 12, color: textColor, opacity: 0.45 }}>
        Type your message…
      </div>
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#111111",
          fontSize: 12,
          flexShrink: 0,
        }}
      >
        ➤
      </div>
    </div>

    <div
      style={{
        display: "flex",
        gap: 5,
        marginTop: 6,
        flexWrap: "wrap",
      }}
    >
      {(suggestions || ["What are your hours?", "Pricing", "Support"]).map((q) => (
        <span
          key={q}
          style={{
            fontSize: 10,
            padding: "2px 8px",
            borderRadius: 6,
            border: `1px solid ${borderColor}`,
            background: inputBg,
            color: textColor,
            whiteSpace: "nowrap",
            opacity: 0.8,
          }}
        >
          {q}
        </span>
      ))}
    </div>
  </div>
);

const Footer = ({ bg, borderColor, textColor, brand }) => (
  <div
    style={{
      padding: "5px 12px",
      borderTop: `1px solid ${borderColor}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: 10,
      color: textColor,
      background: bg,
      flexShrink: 0,
    }}
  >
    <span>
      Powered by{" "}
      <span style={{ color: brand, fontWeight: 600 }}></span>
    </span>
    <span style={{ fontSize: 14, opacity: 0.5 }}>✉</span>
  </div>
);

const SampleQuestionsList = ({ questions, textColor, borderColor, inputBg, onQuestionClick }) => {
  const defaultQuestions = [
    "What services do you offer?",
    "How does pricing work?",
    "Tell me about your AI solutions",
    "Do you offer customer support?",
  ];
  const displayQuestions = questions?.length > 0 ? questions : defaultQuestions;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {displayQuestions.map((question, index) => (
        <div
          key={index}
          style={{
            backgroundColor: inputBg,
            border: `1px solid ${borderColor}`,
            borderRadius: 8,
            padding: "10px 14px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          onClick={() => onQuestionClick && onQuestionClick(question)}
        >
          <span style={{ fontSize: 13, fontWeight: 500, color: textColor }}>
            {question}
          </span>
          <span style={{ color: textColor, opacity: 0.6, fontSize: 12 }}>→</span>
        </div>
      ))}
    </div>
  );
};

const ExpandedChatWindow = ({
  colors,
  company,
  onClose,
  isVisible,
  chatHeaderText,
  isMobile,
  bubbleColor
}) => {
  const {
    bg,
    chatbox,
    textPrimary,
    border,
    inputBg,
    brand,
    headerBg,
    headerText,
    footerBg,
    footerText,
  
  } = colors;

  const [message, setMessage] = useState("");

  if (!isVisible) return null;

  const handleSendMessage = () => {
    if (message.trim()) setMessage("");
  };

  const windowStyle = isMobile
    ? {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        top: "40px",
        width: "100%",
        height: "calc(100% - 40px)",
        borderRadius: "12px 12px 0 0",
        boxShadow: "0 -4px 24px rgba(0,0,0,0.15)",
      }
    : {
        position: "absolute",
        bottom: "90px",
        right: "0",
        width: "clamp(280px, 80%, 350px)",
        height: "min(500px, calc(100% - 110px))",
        borderRadius: "12px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
      };

  return (
    <div
      style={{
        ...windowStyle,
        backgroundColor: bg,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 1001,
        animation: "slideUp 0.3s ease",
    
        maxHeight: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: `1px solid ${border}`,
          background:  bubbleColor,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "#ffffff" }}>
            {chatHeaderText || company || "Ansera AI"}
          </span>
        </div>
        <div
          onClick={onClose}
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: "rgba(255,255,255,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          ×
        </div>
      </div>

      <div
        style={{
          flex: "1 1 0",
          minHeight: 0,
          overflowY: "auto",
          padding: "14px",
          background: bg,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <SampleQuestionsList
          questions={[]}
          textColor={textPrimary}
          borderColor={border}
          inputBg={inputBg}
        />
      </div>

      <div
        style={{
          padding: "10px 12px",
          borderTop: `1px solid ${border}`,
          background: bg,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: inputBg,
            border: `1px solid ${border}`,
            borderRadius: 10,
            padding: "8px 12px",
          }}
        >
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            placeholder="Ask me a question..."
            style={{
              flex: 1,
              fontSize: 13,
              color: textPrimary,
              background: "transparent",
              border: "none",
              outline: "none",
              minWidth: 0,
            }}
          />
          <div
            onClick={handleSendMessage}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: brand,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              fontSize: 14,
              flexShrink: 0,
              cursor: "pointer",
            }}
          >
            ➤
          </div>
        </div>
      </div>

      <Footer bg={footerBg} borderColor={border} textColor={footerText} brand={brand} />

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

function ChatBotPreview({ colors, company, inputPos }) {
  const { bg, chatbox, textPrimary, border, inputBg, brand, headerBg, headerText, footerBg, footerText, buttonBg } = colors;
  // console.log(border,"border")
  const header = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        borderBottom: `1px solid ${border}`,
        background: headerBg,
        flexShrink: 0,
        cursor: "move",
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: headerText,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {company}
      </span>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
        <span
          style={{
            fontSize: 10,
            padding: "3px 8px",
            borderRadius: 6,
            background: buttonBg || "#111111",
            color: "#fff",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          📅 Book
        </span>
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            background: alpha(buttonBg || "#111111", 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            color: brand,
          }}
        >
          ×
        </span>
      </div>
    </div>
  );

  const body = (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "10px 12px",
        background: bg,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minWidth: 0,
      }}
    >
      <SampleQuestion text="What services do you offer?" color={textPrimary} />
      <AnswerBubble
        bg={chatbox}
        textColor={textPrimary}
        borderColor={border}
        brand={brand}
        inputBg={inputBg}
        buttonBg={buttonBg}
      />
    </div>
  );

  const input = (
    <InputArea
      bg={bg}
      inputBg={inputBg}
      borderColor={border}
      textColor={textPrimary}
      brand={brand}
    />
  );

  const footer = (
    <Footer bg={footerBg} borderColor={border} textColor={footerText} brand={brand} />
  );

  return (
    <>
      {header}
      {inputPos === "top" ? (
        <>{input}{body}</>
      ) : (
        <>{body}{input}</>
      )}
      {footer}
    </>
  );
}

const FloatingChatBubble = ({ iconUrl, bubbleColor, onClick, isChatOpen, compact }) => (
  <div
    style={{
      position: "relative",
      cursor: "pointer",
      animation: !isChatOpen ? "float 2s ease-in-out 3" : "none",
    }}
    onClick={onClick}
  >
    <div
      style={{
        position: "relative",
        width: compact ? "72px" : "110px",
        height: compact ? "46px" : "70px",
        boxSizing: "border-box",
        backgroundColor: bubbleColor,
        borderRadius: "20px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: compact ? "8px" : "12px",
        textAlign: "center",
        boxShadow: "0 8px 32px rgba(31,72,75,0.3)",
        transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        border: "2px solid rgba(255,255,255,0.1)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.05) translateY(-4px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <img
        src={iconUrl}
        alt="Ansera Logo"
        style={{
          width: compact ? "22px" : "32px",
          height: compact ? "22px" : "32px",
          objectFit: "contain",
          marginBottom: "3px",
          filter: "brightness(0) invert(1)",
        }}
      />
      <p
        style={{
          fontSize: compact ? "9px" : "12px",
          color: "white",
          margin: 0,
          fontWeight: 700,
          letterSpacing: "0.5px",
          marginTop: "-5px",
        }}
      >
        Ask me
      </p>
    </div>
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-18px); }
      }
    `}</style>
  </div>
);

const BubblePreviewSide = ({ colors, iconUrl, bubbleColor, companyName, chatHeaderText }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const containerRef = useRef(null);
  const containerWidth = useContainerWidth(containerRef);
  const isMobile = containerWidth < 360;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        background: "#f0f2f5",
        borderRadius: "12px",
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${colors.border}`,
      }}
    >
  
      {/* <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "40px",
          background: "#2d3748",
          display: "flex",
          alignItems: "center",
          padding: "0 16px",
          gap: "8px",
          zIndex: 10,
        }}
      >
        {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
          <div
            key={c}
            style={{ width: "12px", height: "12px", borderRadius: "50%", background: c }}
          />
        ))}
        <div
          style={{
            flex: 1,
            height: "24px",
            background: "rgba(255,255,255,0.1)",
            borderRadius: "6px",
            marginLeft: "8px",
          }}
        />
      </div> */}

      {/* FIX 4: Page content — overflow: "hidden" clips the absolutely-positioned
          chat window so it cannot bleed outside this preview frame */}
      <div
        style={{
          position: "absolute",
          top: 40,
          left: 0,
          right: 0,
          bottom: 0,
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{ textAlign: "center", color: "#718096" }}>

          {/* <div style={{ fontSize: isMobile ? "11px" : "14px" }}>Your Website Content</div>
          <div style={{ fontSize: isMobile ? "10px" : "12px", marginTop: "8px" }}>
            Click the chat bubble to open
          </div> */}
        </div>
      </div>

      {/* Floating bubble */}
      <div
        style={{
          position: "absolute",
          bottom: isMobile ? "12px" : "20px",
          right: isMobile ? "12px" : "20px",
          zIndex: 1000,
        }}
      >
        <FloatingChatBubble
          iconUrl={iconUrl}
          bubbleColor={bubbleColor}
          onClick={() => setIsChatOpen(!isChatOpen)}
          isChatOpen={isChatOpen}
          compact={isMobile}
        />
      </div>

      <ExpandedChatWindow
        colors={colors}
        company={companyName}
        onClose={() => setIsChatOpen(false)}
        isVisible={isChatOpen}
        chatHeaderText={chatHeaderText}
        isMobile={isMobile}
        bubbleColor={bubbleColor}
        
      />
    </div>
  );
};


function SearchBarPreview({ colors, company, iconUrl }) {
  const { bg, textPrimary, textSec, border, inputBg, brand } = colors;
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: `1px solid ${border}`,
          background: bg,
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <img src={iconUrl} alt="icon" style={{ width: 20, height: 20, objectFit: "contain", flexShrink: 0 }} />
          <span
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {company}
          </span>
        </div>
        <span style={{ fontSize: 18, color: textSec, lineHeight: 1, flexShrink: 0 }}>×</span>
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px 16px",
          gap: 14,
          background: bg,
          minWidth: 0,
        }}
      >
        <img src={iconUrl} alt="icon" style={{ width: 40, height: 40, objectFit: "contain" }} />
        <p style={{ fontSize: 12.5, color: textSec, textAlign: "center", margin: 0 }}>
          Search your site using AI
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 14px",
            borderRadius: 24,
            border: `1.5px solid ${brand}`,
            width: "100%",
            maxWidth: 300,
            background: inputBg,
            boxShadow: `0 2px 8px ${alpha(brand, 0.15)}`,
            boxSizing: "border-box",
          }}
        >
          <img src={iconUrl} alt="" style={{ width: 18, height: 18, objectFit: "contain", flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 12.5, color: textSec, minWidth: 0 }}>Search anything…</span>
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: border,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              color: textSec,
              flexShrink: 0,
            }}
          >
            ×
          </span>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
          {["How does this work?", "Pricing", "Contact us"].map((q) => (
            <span
              key={q}
              style={{
                fontSize: 10,
                padding: "3px 9px",
                borderRadius: 6,
                border: `1px solid ${border}`,
                color: textSec,
                background: inputBg,
              }}
            >
              {q}
            </span>
          ))}
        </div>
      </div>
      <Footer bg={bg} borderColor={border} textColor={textSec} brand={brand} />
    </>
  );
}

function BubblePreview({ colors, iconUrl, bubbleColor }) {
  const { bg, textSec } = colors;
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        padding: 20,
        background: bg,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 28, opacity: 0.2 }}>👁‍🗨</span>
        <span style={{ fontSize: 11.5, color: textSec, opacity: 0.6 }}>
          Widget hidden — only bubble shown
        </span>
      </div>
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: bubbleColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 12px rgba(0,0,0,0.22)",
          position: "relative",
          zIndex: 2,
          cursor: "pointer",
        }}
      >
        <img
          src={iconUrl}
          alt="open"
          style={{ width: 26, height: 26, objectFit: "contain" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>
    </div>
  );
}
const SearchBarPreviewSide = ({ colors, iconUrl, bubbleColor, companyName, suggestions }) => {
  const { bg, textPrimary, border, inputBg, brand, buttonBg } = colors;
  // console.log(border,"border")

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "#f0f2f5",
        borderRadius: "12px",
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${border}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "center",
        padding: "0 12px 16px",
      }}
    >

      <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <div
          style={{
            border: `2px solid ${border}`,
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "3.2rem",
            backgroundColor: bg,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            padding: "0 8px 0 10px",
            position: "relative",
          }}
        >
          <div
            style={{
              height: "22px",
              width: "22px",
              marginRight: "8px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={iconUrl}
              alt="logo"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
          <span style={{ flex: 1, fontSize: "12px", color: textPrimary, opacity: 0.4 }}>
            Ask me anything...
          </span>
          <span style={{ padding: "4px", color: colors.textSec, fontSize: "14px", marginRight: "4px" }}>
            ⟳
          </span>
          <span style={{ padding: "4px", color: colors.textSec, fontSize: "13px", marginRight: "2px" }}>
            ➤
          </span>

          <span
            style={{
              background: "none",
              color: textPrimary,
              border: "none",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px",
              fontWeight: 500,
              lineHeight: 1,
              flexShrink: 0,
              cursor: "default",
              
            }}
          >
            ×
          </span>
        </div>

        <div style={{ display: "flex", gap: "8px", marginTop: "8px", overflow: "hidden", flexWrap: "nowrap" }}>
          {(suggestions || ["What services?", "Pricing", "Contact us"]).map((q) => (
            <span
              key={q}
              style={{
                fontSize: "10px",
                padding: "4px 12px",
                borderRadius: "16px",
                background: buttonBg || "rgb(51,51,51)",
                color: "#fff",
                whiteSpace: "nowrap",
                cursor: "default",
              }}
            >
              {q}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function WidgetPreview({
  appearance = "chat-bot",
  themeMode = "light",
  customColors = {},
  chatBotColors = {},
  searchIconLogoOption = "teal",
  searchBubbleBgColor = "#219297",
  searchInputPosition = "bottom",
  companyName = "Ansera AI",
}) {
  const [open, setOpen] = useState(false);
  const dialogContentRef = useRef(null);
  const dialogWidth = useContainerWidth(dialogContentRef);

  const isDark = themeMode === "dark";
  const isCustom = themeMode === "custom";

  const brand = chatBotColors.bubbleBackground || "#219297";
  const bubbleColor = searchBubbleBgColor || brand;
  // console.log("brand color:", brand, "bubble color:", bubbleColor, "customColors:", customColors, "isCustom:", isCustom, "isDark:", isDark)

  let colors;
  if (isCustom) {
    colors = {
      bg: customColors.bg || "#ffffff",
      chatbox: customColors.answerbgcolor || "#fdfdfd",
      textPrimary: customColors.text || "#1a202c",
      textSec: customColors.text ? alpha(customColors.text, 0.55) : "#718096",
      border: customColors.inputBorder || "#e2e8f0",
      inputBg: customColors.bg || "#f7fafc",
      brand,
      buttonBg: customColors.buttonBg || "#219297",
      headerBg: customColors.chatheaderBg || "#ffffff",
      headerText: customColors.chatheadertextColor || "#1a202c",
      footerBg: customColors.chatfooterBg || "#f1f1f1",
      footerText: customColors.chatfootertextColor || "#1a202c",
    };
  } else if (isDark) {
    colors = {
      bg: "#13151a",
      chatbox: "#1e2028",
      textPrimary: "#e8eaf0",
      textSec: "#9aa0b0",
      border: "rgba(255,255,255,0.10)",
      inputBg: "#252830",
      brand,
      headerBg: "#1e2028",
      headerText: "#e8eaf0",
      footerBg: "#13151a",
      footerText: "#9aa0b0",
      buttonBg: '#334155'
    };
  } else {
    colors = {
      bg: "#ffffff",
      chatbox: "#fdfdfd",
      textPrimary: "#1a202c",
      textSec: "#718096",
      border: "#219297",
      inputBg: "#f7fafc",
      brand,
      headerBg: "#ffffff",
      headerText: "#1a202c",
      footerBg: "#f9f9f9",
      footerText: "#718096",
      buttonBg:"rgb(51, 51, 51)"
    };
  }

  const iconUrl = ICON_URLS[searchIconLogoOption] || ICON_URLS.teal;

  const stackVertically = dialogWidth < 600;

  const getWidgetContent = () => {
    if (appearance === "chat-bot") {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: stackVertically ? "column" : "row",
            gap: 16,
            width: "100%",
            height: "100%",
            minHeight: 0,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: stackVertically ? "300px" : 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "8px",
                color: "#666",
                textAlign: "center",
              }}
            >
              Widget Preview
            </div>
            <div
              style={{
                flex: 1,
                borderRadius: 14,
                overflow: "hidden",
                border: `1px solid ${colors.border}`,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
                background: colors.bg,
                fontFamily: "system-ui, sans-serif",
                minHeight: 0,
              }}
            >
              <ChatBotPreview
                colors={colors}
                company={companyName}
                inputPos={searchInputPosition}
              />
            </div>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: stackVertically ? "300px" : 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "8px",
                color: "#666",
                textAlign: "center",
              }}
            >
              Live Website Preview (Floating Bubble)
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <BubblePreviewSide
                colors={colors}
                iconUrl={iconUrl}
                bubbleColor={bubbleColor}
                companyName={companyName}
                chatHeaderText={customColors.chat_widget_header_text || companyName}
              />
            </div>
          </div>
        </div>
      );
    } 
    else if (appearance === "type-search") {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: stackVertically ? "column" : "row",
        gap: 16,
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div style={{ flex: 1, minWidth: 0, minHeight: stackVertically ? "300px" : 0, display: "flex", flexDirection: "column" }}>
        <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px", color: "#666", textAlign: "center" }}>
          Widget Preview
        </div>
        <div style={{ flex: 1, borderRadius: 14, overflow: "hidden", border: `1px solid ${colors.border}`, display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.16)", background: colors.bg, minHeight: 0 }}>
          <ChatBotPreview colors={colors} company={companyName} inputPos={searchInputPosition} />
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0, minHeight: stackVertically ? "300px" : 0, display: "flex", flexDirection: "column" }}>
        <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px", color: "#666", textAlign: "center" }}>
          Live Website Preview (Search Bar)
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <SearchBarPreviewSide
            colors={colors}
            iconUrl={iconUrl}
            bubbleColor={bubbleColor}
            companyName={companyName}
            suggestions={["What services?", "Pricing", "Contact us"]}
          />
        </div>
      </div>
    </div>
  );
}
    else if (appearance === "type-search") {
      return (
        <div
          style={{
            width: "min(360px, 100%)",
            height: "min(520px, 100%)",
            borderRadius: 14,
            overflow: "hidden",
            border: `1px solid ${colors.border}`,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
            background: colors.bg,
            fontFamily: "system-ui, sans-serif",
            margin: "0 auto",
          }}
        >
          <ChatBotPreview colors={colors} company={companyName} iconUrl={iconUrl} />
        </div>
      );
    } else if (appearance === "search-icon") {
      return (
        <div
          style={{
            width: "min(360px, 100%)",
            height: "min(520px, 100%)",
            borderRadius: 14,
            overflow: "hidden",
            border: `1px solid ${colors.border}`,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
            background: colors.bg,
            fontFamily: "system-ui, sans-serif",
            margin: "0 auto",
          }}
        >
          <BubblePreview
            colors={colors}
            iconUrl={iconUrl}
            bubbleColor={searchBubbleBgColor}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Tooltip title="Preview widget" placement="top">
        <Button
          variant="outlined"
          startIcon={<RemoveRedEyeIcon />}
          onClick={() => setOpen(true)}
          sx={{
            borderRadius: "8px",
            px: { xs: 2, sm: 3 },
            py: 1.5,
            fontWeight: 600,
            textTransform: "none",
            borderColor: "#219297",
            color: "#219297",
            "&:hover": {
              borderColor: "#1a767a",
              backgroundColor: "rgba(33,146,151,0.06)",
            },
          }}
        >
          Preview
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: { xs: "12px", sm: "20px" },
            background: "transparent",
            boxShadow: "none",
            overflow: "visible",
            width: "min(95vw, 900px)",
            maxWidth: "none",
            height: "min(92vh, 900px)",
            maxHeight: "none",
            m: { xs: 1, sm: 2 },
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(0,0,0,0.45)",
          },
        }}
      >
        <DialogContent
          ref={dialogContentRef}
          sx={{
            p: { xs: 1.5, sm: 2 },
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              alignSelf: "flex-end",
              color: "#fff",
              background: "rgba(255,255,255,0.15)",
              "&:hover": { background: "rgba(255,255,255,0.25)" },
              flexShrink: 0,
            }}
            aria-label="Close preview"
          >
            <CloseIcon />
          </IconButton>

          <div
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 4,
              flexShrink: 0,
            }}
          >
            Widget Preview
          </div>

          <div
            style={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              overflow: appearance === "chat-bot" ? "hidden" : "auto",
            }}
          >
            {getWidgetContent()}
          </div>

          <div
            style={{
              marginTop: 8,
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {[
              { label: appearance.replace("-", " "), color: "#219297" },
              { label: themeMode, color: "#718096" },
            ].map(({ label, color }) => (
              <span
                key={label}
                style={{
                  fontSize: 11,
                  padding: "3px 10px",
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  border: `1px solid ${alpha("#ffffff", 0.2)}`,
                  textTransform: "capitalize",
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}